import { useState } from "react";
import { useUnits } from "../hooks/useUnits";
import { fmtWeight, parseWeightInput, type UnitSystem } from "../lib/units";

// Bottom sheet for setting/clearing the self-set daily target (M6) — opened
// by tapping TargetRing on HomeDashboard. Quick chips cover the common
// cases; a custom field takes anything else. Same visual language as
// PinPad's bottom sheet (styles.css's .sheet-overlay/.sheet-card use the
// same fadeIn/sheetUp keyframes the pin pad already defined) but its own
// class names so this M6 CSS section stays independently append-only.

const QUICK_CHIPS = [500, 1000, 1500, 2000];

function fmtGrams(g: number, system: UnitSystem): string {
  if (system === "imperial") return fmtWeight(g, system);
  return g >= 1000 ? `${(g / 1000).toFixed(g % 1000 ? 1 : 0)}kg` : `${g}g`;
}

export function TargetSheet({
  open, currentGrams, onPick, onClear, onClose,
}: {
  open: boolean;
  currentGrams: number | null;
  onPick: (grams: number) => void;
  onClear: () => void;
  onClose: () => void;
}) {
  const units = useUnits();
  const [custom, setCustom] = useState("");
  if (!open) return null;

  function submitCustom() {
    const n = parseFloat(custom);
    if (Number.isFinite(n) && n > 0) onPick(Math.round(parseWeightInput(n, units)));
  }

  return (
    <div className="sheet-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="panel sheet-card" data-testid="target-sheet">
        <h2 style={{ textTransform: "none", letterSpacing: 0, color: "var(--foreground)", fontSize: "1.15rem" }}>
          Daily target
        </h2>
        <p className="small muted" style={{ marginTop: 4 }}>Pick a {units === "imperial" ? "weight" : "grams"} goal for today — just for you.</p>
        <div className="chip-grid" style={{ marginTop: 12 }}>
          {QUICK_CHIPS.map((g) => (
            <button
              key={g}
              type="button"
              className={`chip${currentGrams === g ? " active" : ""}`}
              data-testid={`target-chip-${g}`}
              onClick={() => onPick(g)}
            >
              {fmtGrams(g, units)}
            </button>
          ))}
        </div>
        <div className="field" style={{ marginTop: 14, marginBottom: 0 }}>
          <label className="field-label" htmlFor="custom-target">{units === "imperial" ? "Custom (oz)" : "Custom (grams)"}</label>
          <input
            id="custom-target"
            data-testid="target-custom-input"
            type="number"
            inputMode="decimal"
            min="0"
            step={units === "imperial" ? "0.5" : "10"}
            placeholder={units === "imperial" ? "e.g. 32" : "e.g. 900"}
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
          />
        </div>
        <div className="btn-row" style={{ marginTop: 14 }}>
          <button type="button" className="btn btn-ghost" data-testid="target-clear" onClick={onClear}>
            Clear target
          </button>
          <button
            type="button"
            className="btn btn-primary"
            data-testid="target-set-custom"
            disabled={!custom}
            onClick={submitCustom}
          >
            Set custom
          </button>
        </div>
        <button
          type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 10, width: "100%" }} onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );
}
